import { useTranslation } from "react-i18next";
import { clip, parseFailure, type Reason } from "./humanize";

/**
 * A value as the harness printed it. Quotes are kept on purpose: `'0'` and
 * `0` are different findings, and the difference is often the whole bug.
 */
function V({ children }: { children: string }) {
  return (
    <code
      title={children.length > 120 ? children : undefined}
      className="break-words rounded bg-black/5 px-1 font-mono text-[0.92em] dark:bg-white/10"
    >
      {clip(children)}
    </code>
  );
}

/** The first line of a multi-line stderr: the rest is in the raw message. */
const head = (s: string) => s.split("\n").find((l) => l.trim() !== "")?.trim() ?? "";

/** One reason, as a sentence in the reader's language. */
export function ReasonText({ reason, nested = false }: { reason: Reason; nested?: boolean }) {
  const { t } = useTranslation();
  switch (reason.kind) {
    case "timeout":
      return (
        <>
          {t("human.timeout", { secs: reason.secs, count: reason.attempts })}
          {reason.inner && (
            <>
              {" "}
              <ReasonText reason={reason.inner} nested />
            </>
          )}
          {reason.truncated && <span className="opacity-70"> {t("human.truncated")}</span>}
        </>
      );
    case "rc":
      return (
        <>
          {/* Short on purpose: inside "after 60 s and 19 attempts:" the long
              form ran past two lines and the clamp cut its ending off. */}
          {t(nested ? "human.rcNested" : "human.rc", {
            rc: reason.rc,
            want: reason.want.split(/,\s*/).join("/"),
          })}
          {reason.stderr ? (
            <>
              : <V>{head(reason.stderr)}</V>
            </>
          ) : (
            <>, {t("human.noMessage")}</>
          )}
          {reason.truncated && <span className="opacity-70"> {t("human.truncated")}</span>}
        </>
      );
    case "ne":
      return (
        <>
          <V>{reason.path}</V> = <V>{reason.got}</V>, {t("human.expected")} <V>{reason.want}</V>
        </>
      );
    case "unchanged":
      return (
        <>
          <V>{reason.path}</V> = <V>{reason.got}</V> — {t("human.unchanged")}
        </>
      );
    case "notContains":
      return (
        <>
          <V>{reason.path}</V> = <V>{reason.got}</V> {t("human.notContains")} <V>{reason.want}</V>
        </>
      );
    case "noMatch":
      return (
        <>
          <V>{reason.path}</V> = <V>{reason.got}</V> {t("human.noMatch")}{" "}
          <V>{`/${reason.pattern}/`}</V>
        </>
      );
    case "cmp":
      return (
        <>
          <V>{reason.path}</V> = <V>{reason.got}</V>, {t("human.expected")}{" "}
          <V>{`${reason.op} ${reason.want}`}</V>
        </>
      );
    case "range":
      return (
        <>
          <V>{reason.path}</V> = <V>{reason.got}</V>{" "}
          {t("human.outOfRange", { lo: reason.lo, hi: reason.hi })}
        </>
      );
    case "missing":
      return (
        <>
          <V>{reason.path}</V> {t("human.missing")}
        </>
      );
    case "stdoutLacks":
      return (
        <>
          {t("human.stdoutLacks")} <V>{reason.want}</V>
        </>
      );
    case "stdoutHas":
      return (
        <>
          {t("human.stdoutHas")} <V>{reason.want}</V>
        </>
      );
    case "stdoutNe":
      return (
        <>
          {t("human.stdoutNe")} <V>{reason.got}</V>, {t("human.expected")} <V>{reason.want}</V>
        </>
      );
    case "stdoutSame":
      return (
        <>
          {t("human.stdoutSame")} <V>{reason.got}</V>
        </>
      );
    case "httpTimeout":
      return <>{t("human.httpTimeout", { host: reason.host, port: reason.port, secs: reason.secs })}</>;
    case "httpDropped":
      return <>{t("human.httpDropped")}</>;
    case "notJson":
      return <>{t("human.notJson")}</>;
    case "cmdTimeout":
      return (
        <>
          {t("human.cmdTimeout", { secs: reason.secs })}: <V>{reason.cmd}</V>
        </>
      );
    case "cmdFailed":
      return (
        <>
          {t("human.cmdFailed", { rc: reason.rc })}: <V>{reason.cmd}</V>
          {reason.stderr ? <> — {head(reason.stderr)}</> : <>, {t("human.noMessage")}</>}
        </>
      );
    case "exception":
      return (
        <>
          <span className="font-medium">{reason.name}</span>: {clip(reason.text, 300)}
        </>
      );
    case "text":
      return <>{clip(reason.text, 400)}</>;
  }
}

/**
 * Why a case failed, as the row shows it: which step, and the reason in
 * words. The harness's own text stays one hover away, and whole in the step
 * record below.
 */
export function FailureSummary({ message }: { message: string }) {
  const { t } = useTranslation();
  const f = parseFailure(message);
  return (
    <>
      {f.step !== undefined && (
        <span className="mr-1.5 inline-block rounded bg-red-100 px-1.5 text-[11px] font-medium text-red-800 dark:bg-red-900/60 dark:text-red-100">
          {t("human.step", { n: f.step })}
          {f.action ? ` · ${f.action}` : ""}
        </span>
      )}
      <ReasonText reason={f.reason} />
    </>
  );
}
